import React from "react"
import { cn } from "../../lib/utils"
import DOMPurify from "isomorphic-dompurify"

export interface HtmlContentRendererProps {
  content: string
  contentType?: "plain" | "html"
  className?: string
}

/**
 * Force every link in message content to open in a new tab, safely.
 *
 * Two separate reasons, and both matter:
 *
 * - **The panel is a transient surface.** Navigating in place throws away whatever the reader was
 *   doing — an unsent draft, a scroll position, the grid selection behind it. Message bodies are the
 *   one place in this component where a click can leave the app entirely.
 * - **`rel` is a security requirement, not a nicety.** Message content is authored by other people,
 *   including clients. A `target="_blank"` link without `noopener` hands the opened page a handle on
 *   `window.opener` and lets it redirect the tab it came from. Setting it here rather than trusting
 *   the author is the whole point.
 *
 * Done as a DOMPurify hook so it applies to sanitized output — an attribute added before sanitizing
 * would simply be stripped again.
 */
const ANCHOR_HOOK = "afterSanitizeAttributes"

const forceExternalAnchors = (node: Element) => {
  if (node.nodeName === "A") {
    node.setAttribute("target", "_blank")
    node.setAttribute("rel", "noopener noreferrer")
  }
}

const sanitizeHtml = (html: string): string => {
  // Scope the hook to THIS call. isomorphic-dompurify exports one shared instance and addHook is
  // global state on it, so a permanently-installed hook would force target/rel onto every anchor
  // sanitized anywhere else in the consuming app. sanitize() is synchronous, so nothing interleaves
  // between add and remove.
  DOMPurify.addHook(ANCHOR_HOOK, forceExternalAnchors)
  try {
    return DOMPurify.sanitize(html, {
      USE_PROFILES: {
        html: true
      },
      // The html profile otherwise permits form controls and inline style, so a client-authored message
      // could render a fake in-app sign-in form (credentials POST to an attacker's host) or a
      // full-viewport overlay — both seen by internal staff inside trusted panel chrome. The Tiptap
      // editor emits none of these, so forbidding them costs nothing.
      FORBID_TAGS: ["form", "input", "button", "select", "textarea", "style"],
      FORBID_ATTR: ["style", "action", "formaction"],
      // Without these, the hook's own attributes are stripped straight back off.
      ADD_ATTR: ["target", "rel"]
    })
  } finally {
    // Pops the hook we just pushed, leaving any host-registered hooks intact.
    DOMPurify.removeHook(ANCHOR_HOOK)
  }
}

export function HtmlContentRenderer({ content, contentType = "html", className }: HtmlContentRendererProps) {
  /**
   * Let a link click reach the browser.
   *
   * Message content sits inside surfaces that treat a click as "select this row" or "open this
   * thread", and those ancestors call `preventDefault`. That is what makes a link look dead on left
   * click while middle-click and right-click → open both work — the browser's own handling is fine,
   * something above it is cancelling the default. Stopping propagation at the anchor keeps the
   * ancestor's behaviour everywhere else in the message.
   */
  const handleClick = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const anchor = (event.target as HTMLElement | null)?.closest?.("a")
    if (anchor && event.currentTarget.contains(anchor)) {
      event.stopPropagation()
    }
  }, [])

  if (contentType === "html") {
    const sanitizedContent = sanitizeHtml(content)

    return (
      <>
        {/* Inline CSS to ensure WYSIWYG behavior - isolated to this component */}
        <style>{`
          .html-content-renderer p {
            margin: 0.5em 0;
            line-height: 1.4;
          }
          .html-content-renderer ul {
            margin: 0.5em 0;
            padding-left: 1.5em;
            list-style-type: disc;
          }
          .html-content-renderer ol {
            margin: 0.5em 0;
            padding-left: 1.5em;
            list-style-type: decimal;
          }
          .html-content-renderer li {
            margin: 0.2em 0;
            line-height: 1.4;
          }
          .html-content-renderer strong, .html-content-renderer b {
            font-weight: bold;
          }
          .html-content-renderer em, .html-content-renderer i {
            font-style: italic;
          }
          .html-content-renderer u {
            text-decoration: underline;
          }
          .html-content-renderer a {
            color: #0066cc;
            text-decoration: underline;
            cursor: pointer;
          }
          .html-content-renderer a:hover {
            color: #004499;
          }
          .html-content-renderer br {
            display: block;
            margin: 0.2em 0;
            content: "";
          }
          /* Remove any external styling interference */
          .html-content-renderer * {
            box-sizing: border-box;
          }
        `}</style>
        <div
          className={cn("html-content-renderer", className)}
          onClick={handleClick}
          dangerouslySetInnerHTML={{ __html: sanitizedContent }}
        />
      </>
    )
  }

  // Plain text - preserve line breaks
  return (
    <div className={cn("plain-content-renderer", className)} style={{ whiteSpace: 'pre-wrap' }}>
      {content}
    </div>
  )
}
